import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { collection, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { Unsubscribe } from 'firebase/firestore';
import { Subscription } from 'rxjs';
import { dataStore, db } from '../../../app.component';
import { User } from '../../../interfaces';
import { Role } from '../../../enums';
import { PermissionsService } from '../../../services/permissions.service';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { MatCard } from '@angular/material/card';
import { MatChipSet, MatChip } from '@angular/material/chips';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { CdkVirtualScrollViewport, CdkFixedSizeVirtualScroll } from '@angular/cdk/scrolling';
import { SendScheduleDialogComponent } from '../../dialogs/send-schedule/send-schedule-dialog.component';

@Component({
  selector: 'app-schedule-employee-list',
  templateUrl: './employee-list.component.html',
  styleUrls: ['./employee-list.component.scss'],
  imports: [
    MatIcon,
    MatIconButton,
    MatCard,
    MatChipSet,
    MatChip,
    MatFormField,
    MatLabel,
    MatSuffix,
    MatInput,
    ReactiveFormsModule,
    CdkVirtualScrollViewport,
    CdkFixedSizeVirtualScroll,
  ],
})
export class ScheduleEmployeeListComponent implements OnInit, OnDestroy {
  private readonly organisationId = dataStore.selectedOrganisation.id;
  private unsubscribe?: Unsubscribe;
  private searchSubscription?: Subscription;

  allEmployees = signal<User[]>([]);
  filteredEmployees = signal<User[]>([]);
  selectedRole = signal<Role | null>(null);
  showBackButton = signal(true);

  dataStore = dataStore;
  RoleEnum = Role;

  searchControl = new FormControl('');

  constructor(
    private router: Router,
    private permissionsService: PermissionsService,
    private dialog: MatDialog,
  ) {}

  canSendSchedule(): boolean {
    return (
      dataStore.rootUser?.rights === 'admin' ||
      this.permissionsService.hasAtLeastRole(dataStore.orgUser?.roles, Role.Planner)
    );
  }

  openSendScheduleDialog(): void {
    this.dialog.open(SendScheduleDialogComponent, {
      width: '500px',
      disableClose: true,
      data: {
        orgId: dataStore.selectedOrganisation.id,
        coDriverEnabled: dataStore.generalSettings?.coDriverEnabled ?? false,
      },
    });
  }

  ngOnInit() {
    const roles = dataStore.orgUser?.roles ?? [];
    const scheduleRoles = [Role.Driver, Role.CoDriver, Role.Planner, Role.RidePlanner];
    this.showBackButton.set(
      roles.some((role) => scheduleRoles.includes(role)) ||
        roles.includes(Role.Administrator) ||
        dataStore.rootUser?.rights === 'admin',
    );

    this.searchSubscription = this.searchControl.valueChanges.subscribe(() => this.applyFilter());

    const queryRef = query(
      collection(db, `organisations/${this.organisationId}/users/`),
      where('roles', 'array-contains-any', [
        Role.Driver,
        Role.CoDriver,
        Role.Planner,
        Role.RidePlanner,
      ]),
      orderBy('name'),
    );

    this.unsubscribe = onSnapshot(queryRef, (snapshot) => {
      const users: User[] = [];
      snapshot.forEach((doc) => {
        users.push({ id: doc.id, ...doc.data() } as User);
      });
      this.allEmployees.set(users);
      this.applyFilter();
    });
  }

  ngOnDestroy() {
    this.searchSubscription?.unsubscribe();
    this.unsubscribe?.();
  }

  private applyFilter() {
    const lower = (this.searchControl.value ?? '').toLowerCase().trim();
    const role = this.selectedRole();
    this.filteredEmployees.set(
      this.allEmployees().filter((u) => {
        const roles = u.roles ?? [];
        const matchesRole =
          !role ||
          (role === Role.Planner
            ? roles.includes(Role.Planner) || roles.includes(Role.RidePlanner)
            : roles.includes(role));
        const matchesSearch =
          !lower ||
          (u.name ?? '').toLowerCase().includes(lower) ||
          (u.lastName ?? '').toLowerCase().includes(lower) ||
          (u.email ?? '').toLowerCase().includes(lower) ||
          (u.phone ?? u.phoneNumber ?? '').includes(lower);
        return matchesRole && matchesSearch;
      }),
    );
  }

  toggleRole(role: Role) {
    this.selectedRole.set(this.selectedRole() === role ? null : role);
    this.applyFilter();
  }

  clearSearch() {
    this.searchControl.setValue('');
    this.applyFilter();
  }

  openEmployee(user: User) {
    const highestRole = this.permissionsService.getHighestRole(user.roles);
    let type: string;
    switch (highestRole) {
      case Role.Driver:
        type = 'driver';
        break;
      case Role.CoDriver:
        type = 'co-driver';
        break;
      default:
        type = 'planner';
    }
    this.router.navigate(['/schedule', type, user.id], {
      state: { from: 'employees' },
    });
  }

  goBack() {
    this.router.navigate(['/schedule']);
  }

  getRoleLabel(role: string): string {
    const labels: Record<string, string> = {
      driver: 'Chauffeur',
      co_driver: 'Bijrijder',
      planner: 'Planner',
      rideplanner: 'Ritplanner',
    };
    return labels[role] ?? role;
  }
}
